import { ThemeProvider } from '@mui/material/styles';
import CssBaseline from '@mui/material/CssBaseline';
import * as Sentry from '@sentry/react';
import { CookiesProvider } from 'react-cookie';
import ReactDOM from 'react-dom/client';
import { HelmetProvider } from 'react-helmet-async';
import { RouterProvider } from 'react-router-dom';
import { Suspense } from 'react';
import { ToastContainer, Slide } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import './styles/react-toastify-override.scss';
import MainLoading from './MainLoading';
import theme from './pages/DDM/Theme/defaultTheme';
import router from './router';
import { loginWithToken, isLogined } from './services/auth.service';
import { useUserStore } from './store/userStore';

const env = import.meta.env.VITE_SET_ENV || 'DEV';

Sentry.init({
    dsn: 'https://9326bdeea8b8c28c4a8b6c982560b649@o4508776190377984.ingest.us.sentry.io/4508804727373824',
    integrations: [Sentry.browserTracingIntegration(), Sentry.replayIntegration()],
    environment: env || 'DEV',
    tracesSampleRate: 1.0,
    replaysSessionSampleRate: 0.1,
    replaysOnErrorSampleRate: 1.0,
    enabled: env === 'PRD',
    normalizeDepth: 6,
    attachStacktrace: true,
});

if (env === 'PRD') {
    // if (!window.console) {
    //     window.console = {};
    // }
    const methods = ['log', 'debug', 'warn', 'info'];
    for (let i = 0; i < methods.length; i++) {
        window.console[methods[i]] = function () {};
    }
}

// anonymousUser-storage를 localStorage에서 sessionStorage로 이전하는 과정에서
// 보통은 zustand에서 sessionStorage에 있는 데이터를 가져오겠지만 만일의 사태에 대비하여
// localStorage에 남아있는 anonymousUser-storage를 삭제하는 로직을 추가
// 추후 이 로직을 남길지 삭제할 지는 TODO: 로 남김
const anonymousLocalData = localStorage.getItem('anonymousUser-storage');
const anonymousSessionData = sessionStorage.getItem('anonymousUser-storage');
if (anonymousSessionData) {
    if (anonymousLocalData) {
        localStorage.removeItem('anonymousUser-storage');
    }
}

// URL 파라미터에서 토큰 확인 및 자동 로그인 처리
const handleTokenLogin = async () => {
    try {
        const urlParams = new URLSearchParams(window.location.search);
        const token = urlParams.get('token');

        if (token && !isLogined()) {
            await loginWithToken(token);

            // URL에서 토큰 파라미터 제거 (보안상 이유)
            const newUrl = new URL(window.location.href);
            newUrl.searchParams.delete('token');
            window.history.replaceState({}, '', newUrl.toString());

            // 로그인 완료 후 상태 재확인
            const loginStatusAfter = isLogined();
            if (loginStatusAfter) return;
        }
    } catch (error) {
        // token 인증 실패 시 URL에서 token 파라미터 제거 후 reload
        const newUrl = new URL(window.location.href);
        newUrl.searchParams.delete('token');
        window.location.href = newUrl.toString();
    }
};

// Toast transition 설정 (기본 Slide 사용)

// 에러 핸들러 등록
window.addEventListener('error', (e) => {
    Sentry.captureException(e);
});
window.addEventListener('unhandledrejection', (e) => {
    const error = e.reason;
    Sentry.captureException(error);
});
window.addEventListener('abort', (e) => {
    Sentry.captureException(e);
});

// 앱 초기화 (토큰 로그인 처리 후 React 렌더링)
(async () => {
    await handleTokenLogin();
    ReactDOM.createRoot(document.getElementById('root') as HTMLElement).render(
        <>
            <Sentry.ErrorBoundary>
                <HelmetProvider>
                    <CookiesProvider>
                        <ThemeProvider theme={theme}>
                            <CssBaseline />
                            <Suspense fallback={<MainLoading />}>
                                <RouterProvider router={router} fallbackElement={<MainLoading />} />
                            </Suspense>
                            <ToastContainer
                                closeOnClick
                                draggable
                                transition={Slide}
                                theme={'colored'}
                                hideProgressBar={true}
                                autoClose={3000}
                                pauseOnFocusLoss={false}
                                position="bottom-center"
                            />
                        </ThemeProvider>
                    </CookiesProvider>
                </HelmetProvider>
            </Sentry.ErrorBoundary>
        </>,
    );
})();
